import { useEffect } from 'react'
import { useNavigate, createSearchParams } from 'react-router-dom'
import { XCircle, SaudiRiyal } from 'lucide-react'
import styled from 'styled-components'
import { usePaymentStore, CHECKOUT_STAGE_QUERY_KEY, CHECKOUT_URL_STAGES } from '../store/paymentStore'
import { useCartStore } from '../store/useCartStore'
import * as S from './ResultStyles'

// Card بدون ظل
const CardWithoutShadow = styled(S.Card)`
  box-shadow: none;
`

// Hero مع padding أقل في الأسفل
const HeroWithLessPadding = styled(S.Hero)`
  margin-bottom: 20px;
`

// تصميم بسيط وأنيق
const ProductsContainer = styled.div`
  margin-bottom: 20px;
`

const ProductRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid oklch(0.9683 0.0069 247.9);
  
  &:last-child {
    border-bottom: none;
  }
`

const ProductInfo = styled.div`
  flex: 1;
  text-align: right;
  margin-left: 16px;
`

const ProductName = styled.div`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
`

const ProductDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: oklch(55.44% 0.041 257.42);
  font-size: 13px;
`

const ProductQuantity = styled.span`
  color: oklch(55.44% 0.041 257.42);
`

const ProductPrice = styled.div`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  direction: ltr;
`

const RiyalIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: inherit;
`

const SummaryBox = styled.div`
  background: transparent;
  border: none;
  padding: 0;
`

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
`

const SummaryLabel = styled.span`
  color: oklch(55.44% 0.041 257.42);
  font-size: 15px;
  font-weight: 500;
`

const SummaryValue = styled.span`
  color: oklch(0.61 0.25 28.5);
  font-size: 16px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  direction: ltr;
`

export default function FailurePage() {
  const navigate = useNavigate()
  const { order, paymentResult, resetPayment, clearOrder, setPaymentResult, checkPaymentStatus } = usePaymentStore()
  const { items: cartItems } = useCartStore()

  useEffect(() => {
    // عنوان الصفحة يُحدّث ديناميكياً من App.tsx حسب اسم المتجر
    const params = new URLSearchParams(window.location.search)
    const chargeId = params.get('charge_id')
    
    if (chargeId) {
      window.history.replaceState({}, document.title, window.location.pathname)
      checkPaymentStatus(chargeId)
    } else if (paymentResult?.success) {
      const successToken = (paymentResult.data as any)?.success_token || ''
      navigate(successToken ? `/success/${encodeURIComponent(successToken)}` : '/success', { replace: true })
    } else if (!paymentResult && !chargeId) {
      navigate('/', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const backToCheckout = () => {
    setPaymentResult(null)
    navigate({
      pathname: '/checkout',
      search: `?${createSearchParams({ [CHECKOUT_STAGE_QUERY_KEY]: CHECKOUT_URL_STAGES.PAYMENT }).toString()}`,
    }, { replace: true })
  }

  const backToStore = () => {
    resetPayment()
    clearOrder()
    navigate('/', { replace: true })
  }

  const amount = order?.amount || 0
  const shipping = order?.shipping || 0
  const discount = paymentResult?.data?.discount || 0
  const total = amount + shipping - discount
  
  // استخدام items من الباك اند أولاً (paymentResult.data.items)، ثم order.items، ثم cartItems كحل احتياطي
  let orderItems: Array<{
    id: string
    title: string
    price: number
    quantity: number
    image?: string
    description?: string
  }> = []
  
  // الأولوية: items من استجابة الباك اند
  if (paymentResult?.data?.items && Array.isArray(paymentResult.data.items) && paymentResult.data.items.length > 0) {
    orderItems = paymentResult.data.items.map((item: any) => ({
      id: item.product_id || item.id || '',
      title: item.product_name || item.title || '',
      price: item.price || 0,
      quantity: item.quantity || 1,
      image: item.image,
      description: item.description
    }))
  } else if (order?.items && order.items.length > 0) {
    // الثاني: order.items من الحالة المحلية
    orderItems = order.items
  } else if (cartItems && cartItems.length > 0) {
    // الثالث: cartItems كحل احتياطي
    orderItems = cartItems.map(item => ({
      id: item.id,
      title: item.title,
      price: item.price,
      quantity: item.quantity,
      image: item.image,
      description: item.description
    }))
  }
  
  const status = paymentResult?.status || 'FAILED'

  const cleanMessage = paymentResult?.message?.replace(/✅|❌|⚠️|💰|🚫|💚|ℹ️|✋/g, '').trim() || 'فشل إتمام عملية الدفع'

  const getStatusMessage = () => {
    switch (status) {
      case 'FAILED':
        return 'فشل إتمام عملية الدفع'
      case 'DECLINED':
        return 'تم رفض البطاقة من قبل البنك'
      case 'CANCELLED':
        return 'تم إلغاء العملية'
      default:
        return 'فشل إتمام عملية الدفع'
    }
  }

  return (
    <S.PageContainer>
      <S.Wrapper>
        <CardWithoutShadow>
          <S.CardBody>
            <HeroWithLessPadding>
              <XCircle size={64} style={{ color: 'oklch(0.61 0.25 28.5)' }} />
              <S.Title>{getStatusMessage()}</S.Title>
              <S.Subtitle>{cleanMessage}</S.Subtitle>
            </HeroWithLessPadding>

            {(order || orderItems.length > 0) && (
              <S.Summary>
                {/* المنتجات */}
                {orderItems.length > 0 && (
                  <ProductsContainer>
                    {orderItems.map((item) => (
                      <ProductRow key={item.id}>
                        <ProductInfo>
                          <ProductName>{item.title}</ProductName>
                          <ProductDetails>
                            <ProductQuantity>الكمية: {item.quantity}</ProductQuantity>
                          </ProductDetails>
                        </ProductInfo>
                        <ProductPrice>
                          <RiyalIcon>
                            <SaudiRiyal size={16} strokeWidth={2.5} />
                          </RiyalIcon>
                          {(item.price * item.quantity).toFixed(2)}
                        </ProductPrice>
                      </ProductRow>
                    ))}
                  </ProductsContainer>
                )}

                {/* للتوافق مع الكود القديم - فقط إذا لم تكن هناك منتجات من order.items أو cartItems */}
                {orderItems.length === 0 && order?.product?.title && (
                  <ProductsContainer>
                    <ProductRow>
                      <ProductInfo>
                        <ProductName>{order.product.title}</ProductName>
                        <ProductDetails>
                          <ProductQuantity>الكمية: {order.qty || 1}</ProductQuantity>
                        </ProductDetails>
                      </ProductInfo>
                      <ProductPrice>
                        <RiyalIcon>
                          <SaudiRiyal size={16} strokeWidth={2.5} />
                        </RiyalIcon>
                        {amount.toFixed(2)}
                      </ProductPrice>
                    </ProductRow>
                  </ProductsContainer>
                )}
                
                {/* المبلغ */}
                {amount > 0 && (
                  <SummaryBox>
                    <SummaryItem>
                      <SummaryLabel>المبلغ</SummaryLabel>
                      <SummaryValue>
                        <RiyalIcon>
                          <SaudiRiyal size={16} strokeWidth={2.5} />
                        </RiyalIcon>
                        {total > 0 ? total.toFixed(2) : amount.toFixed(2)}
                      </SummaryValue>
                    </SummaryItem>
                  </SummaryBox>
                )}
              </S.Summary>
            )}

            <S.Actions>
              <S.RetryButton onClick={backToCheckout}>
                المحاولة مرة أخرى
              </S.RetryButton>
              <S.SecondaryButton onClick={backToStore}>
                العودة للمتجر
              </S.SecondaryButton>
            </S.Actions>
          </S.CardBody>
        </CardWithoutShadow>
      </S.Wrapper>
    </S.PageContainer>
  )
}
